/**
 * ListingFilters - Filter controls for property listings
 * 
 * Provides filtering by:
 * - Listing type (Sale, Rent, Auction)
 * - Property type
 * - Price range
 * - Bedrooms/Bathrooms
 * - Status
 */

'use client';

import { useState, useCallback } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import type { ListingType, ListingStatus } from '@/lib/api/clients/listings';
import { getListingTypeLabel, getListingStatusLabel } from './types';

export interface ListingFiltersState {
  search: string;
  listingType: ListingType | 'ALL';
  status: ListingStatus | 'ALL';
  minPrice: number | null;
  maxPrice: number | null;
  minBedrooms: number | null;
  maxBedrooms: number | null;
  minBathrooms: number | null;
  propertyTypeId: string | null;
  sortBy: 'newest' | 'price_asc' | 'price_desc' | 'bedrooms';
}

export interface ListingFiltersProps {
  filters: ListingFiltersState;
  onChange: (filters: ListingFiltersState) => void;
  propertyTypes?: Array<{ id: string; name: string }>;
  showAdvanced?: boolean;
  className?: string;
}

export const defaultFilters: ListingFiltersState = {
  search: '',
  listingType: 'ALL',
  status: 'ALL',
  minPrice: null,
  maxPrice: null,
  minBedrooms: null,
  maxBedrooms: null,
  minBathrooms: null,
  propertyTypeId: null,
  sortBy: 'newest',
};

export function ListingFilters({
  filters,
  onChange,
  propertyTypes = [],
  showAdvanced = true,
  className = '',
}: ListingFiltersProps) {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  
  const updateFilter = useCallback(<K extends keyof ListingFiltersState>(
    key: K,
    value: ListingFiltersState[K]
  ) => {
    onChange({ ...filters, [key]: value });
  }, [filters, onChange]);
  
  const clearFilters = useCallback(() => {
    onChange(defaultFilters);
  }, [onChange]);
  
  const hasActiveFilters = 
    filters.listingType !== 'ALL' ||
    filters.status !== 'ALL' ||
    filters.minPrice !== null ||
    filters.maxPrice !== null ||
    filters.minBedrooms !== null ||
    filters.propertyTypeId !== null;

  const listingTypes: Array<ListingType | 'ALL'> = [
    'ALL', 'SALE', 'RENTAL', 'AUCTION', 'LEASE', 'HOLIDAY_RENTAL'
  ];
  
  const statuses: Array<ListingStatus | 'ALL'> = [
    'ALL', 'ACTIVE', 'COMING_SOON', 'UNDER_OFFER'
  ];

  return (
    <div className={`space-y-3 sm:space-y-4 ${className}`}>
      {/* Main Filter Bar */}
      <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[160px] sm:min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search listings..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
          />
        </div>
        
        {/* Listing Type Filter - visible on sm+ */}
        <div className="relative hidden sm:block">
          <select
            value={filters.listingType}
            onChange={(e) => updateFilter('listingType', e.target.value as ListingType | 'ALL')}
            className="appearance-none pl-4 pr-10 py-2 sm:py-2.5 border border-gray-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none cursor-pointer"
          >
            {listingTypes.map((type) => (
              <option key={type} value={type}>
                {type === 'ALL' ? 'All Types' : getListingTypeLabel(type)}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
        
        {/* Status Filter - visible on md+ */}
        <div className="relative hidden md:block">
          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value as ListingStatus | 'ALL')}
            className="appearance-none pl-4 pr-10 py-2 sm:py-2.5 border border-gray-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none cursor-pointer"
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status === 'ALL' ? 'All Status' : getListingStatusLabel(status)}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
        
        {/* Sort */}
        <div className="relative hidden sm:block">
          <select
            value={filters.sortBy}
            onChange={(e) => updateFilter('sortBy', e.target.value as ListingFiltersState['sortBy'])}
            className="appearance-none pl-4 pr-10 py-2 sm:py-2.5 border border-gray-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none cursor-pointer"
          >
            <option value="newest">Newest First</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="bedrooms">Most Bedrooms</option>
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
        
        {/* Advanced Toggle */}
        {showAdvanced && (
          <button
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border transition-colors ${
              isAdvancedOpen || hasActiveFilters
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <SlidersHorizontal size={16} />
            <span className="text-sm font-medium">Filters</span>
            {hasActiveFilters && (
              <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                {[
                  filters.listingType !== 'ALL',
                  filters.status !== 'ALL',
                  filters.minPrice !== null || filters.maxPrice !== null,
                  filters.minBedrooms !== null,
                  filters.propertyTypeId !== null,
                ].filter(Boolean).length}
              </span>
            )}
          </button>
        )}
        
        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-2 sm:py-2.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>
      
      {/* Advanced Filters Panel */}
      {showAdvanced && isAdvancedOpen && (
        <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Mobile-only: Listing Type & Sort */}
          <div className="sm:hidden">
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={filters.listingType}
              onChange={(e) => updateFilter('listingType', e.target.value as ListingType | 'ALL')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            >
              {listingTypes.map((type) => (
                <option key={type} value={type}>
                  {type === 'ALL' ? 'All Types' : getListingTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
          
          <div className="sm:hidden">
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort</label>
            <select
              value={filters.sortBy}
              onChange={(e) => updateFilter('sortBy', e.target.value as ListingFiltersState['sortBy'])}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            >
              <option value="newest">Newest First</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="bedrooms">Most Bedrooms</option>
            </select>
          </div>
          
          {/* Status (mobile only - visible on md+ in toolbar) */}
          <div className="md:hidden">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value as ListingStatus | 'ALL')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status === 'ALL' ? 'All Status' : getListingStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
          
          {/* Price Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={filters.minPrice ?? ''}
                onChange={(e) => updateFilter('minPrice', e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              />
              <span className="text-gray-400">-</span>
              <input
                type="number"
                placeholder="Max"
                value={filters.maxPrice ?? ''}
                onChange={(e) => updateFilter('maxPrice', e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              />
            </div>
          </div>
          
          {/* Bedrooms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms</label>
            <div className="flex items-center gap-2">
              <select
                value={filters.minBedrooms ?? ''}
                onChange={(e) => updateFilter('minBedrooms', e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              >
                <option value="">Any</option>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n}+</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Bathrooms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bathrooms</label>
            <div className="flex items-center gap-2">
              <select
                value={filters.minBathrooms ?? ''}
                onChange={(e) => updateFilter('minBathrooms', e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              >
                <option value="">Any</option>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>{n}+</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Property Type */}
          {propertyTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
              <select
                value={filters.propertyTypeId ?? ''}
                onChange={(e) => updateFilter('propertyTypeId', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              >
                <option value="">All Types</option>
                {propertyTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Apply filters to a list of listings
 */
export function applyListingFilters(
  listings: Array<{
    listingType: ListingType;
    status: ListingStatus;
    price: { amount: number } | null;
    property: {
      bedrooms: number | null;
      bathrooms: number | null;
      name: string;
    };
    propertyType: { id: string } | null;
    listedDate: string | null;
  }>,
  filters: ListingFiltersState
): typeof listings {
  return listings
    .filter((listing) => {
      // Search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (!listing.property.name.toLowerCase().includes(search)) {
          return false;
        }
      }
      
      // Listing type filter
      if (filters.listingType !== 'ALL' && listing.listingType !== filters.listingType) {
        return false;
      }
      
      // Status filter
      if (filters.status !== 'ALL' && listing.status !== filters.status) {
        return false;
      }
      
      // Price filter
      if (filters.minPrice !== null && (!listing.price || listing.price.amount < filters.minPrice)) {
        return false;
      }
      if (filters.maxPrice !== null && (!listing.price || listing.price.amount > filters.maxPrice)) {
        return false;
      }
      
      // Bedrooms filter
      if (filters.minBedrooms !== null && (listing.property.bedrooms === null || listing.property.bedrooms < filters.minBedrooms)) {
        return false;
      }
      
      // Bathrooms filter
      if (filters.minBathrooms !== null && (listing.property.bathrooms === null || listing.property.bathrooms < filters.minBathrooms)) {
        return false;
      }
      
      // Property type filter
      if (filters.propertyTypeId !== null && listing.propertyType?.id !== filters.propertyTypeId) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      switch (filters.sortBy) {
        case 'newest':
          return (b.listedDate || '').localeCompare(a.listedDate || '');
        case 'price_asc':
          return (a.price?.amount || 0) - (b.price?.amount || 0);
        case 'price_desc':
          return (b.price?.amount || 0) - (a.price?.amount || 0);
        case 'bedrooms':
          return (b.property.bedrooms || 0) - (a.property.bedrooms || 0);
        default:
          return 0;
      }
    });
}

export default ListingFilters;
